import { GetStaticPaths, GetStaticPathsContext, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  SimpleGrid,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import * as Sentry from '@sentry/nextjs';
import { NextSeo } from 'next-seo';
import DOMPurify from 'isomorphic-dompurify';
import { initializeApollo } from '@/lib/apolloClient';
import { fetchEntry, fetchNavigations } from '@/lib/content-api';
import { Field } from '@/types';
import { ListingProps } from '@/types/listing';
import {
  GetNearbyListingsQuery,
  GetPublicListingByPkDocument,
  GetPublicListingByPkQuery,
  GetPublicListingIdsDocument,
  GetPublicListingIdsQuery,
  GetPublicListingIdsQueryVariables,
} from '@/generated/graphql';
import CustomSwiper from '@/components/frontend/CustomSwiper';
import ListingInfoCard from '@/components/frontend/ListingInfoCard';
import ListingCard from '@/components/frontend/ListingCard';
import ForestInformationSection from '@/components/common/ForestInformationSection';
import ContactBlock from '@/components/frontend/ContactBlock';
import QuestionSection from '@/components/frontend/QuestionSection';
import PublicLayout from '@/components/frontend/PublicLayout';
import AttachmentsSection from '@/components/frontend/AttachmentsSection';
import { publicContainerAttachmentUrl } from '@/lib/urls';
import { listingImages } from '@/lib/listingUtils';
import resolveEntryStaticData from '@/lib/content-api/entryDataResolver';
import { splitFields } from '@/components/entry-templates/Page';
import ArticleLayout, {
  FieldsLayout,
} from '@/components/frontend/ArticleLayout';
import Section, { sectionPadding } from '@/components/frontend/FullSection';
import { ForestDataLocales } from '@/types/forestData';
import Paragraph from '@/components/frontend/Paragraph';
import getRevalidateValue from '@/lib/getRevalidateValue';
import { getListingForestryData, isValidLocale } from '@/lib/forestryData';
import { PropertyTabsProvider } from '@/components/frontend/PropertyTabs';
import fetchAPI from '@/lib/graphqlFetch';

const ListingDetailsSection = dynamic(
  () => import('@/components/frontend/ListingDetailsSection'),
);
const ListingDetailsTable = dynamic(
  () => import('@/components/frontend/ListingDetailsTable'),
);
const ListingBuyerCosts = dynamic(
  () => import('@/components/frontend/ListingBuyerCosts'),
);

const Fields = dynamic(() => import('@/components/frontend/Fields'));

interface Props extends ListingProps {
  nearbyListings: GetNearbyListingsQuery['nearby_listings'];
  isPreview?: boolean;
  sectionFields: Field[];
}

const SingleListingPage = ({
  listing,
  forestData,
  nearbyListings,
  isPreview,
  sectionFields,
}: Props) => {
  const { t } = useTranslation('frontoffice');
  const { propertyImages } = listingImages(listing);

  const sections = sectionFields?.length ? (
    <Fields fields={sectionFields} />
  ) : null;

  if (!listing) {
    return (
      <FieldsLayout>
        <Text>{t('listing_not_found', 'Kohdetta ei löytynyt')}</Text>
        {sections}
      </FieldsLayout>
    );
  }

  return (
    <>
      <NextSeo
        title={`${listing.name || ''} - ${listing.public_id || ''}`}
        description={listing.description_short || ''}
      />
      <Box
        justifyContent="center"
        alignItems="center"
        minHeight="500px"
        sx={{
          display: 'none',
          '@media print': { display: 'flex' },
        }}
      >
        <Text fontSize="xl" align="center" maxWidth="400px" margin="0 auto">
          {t(
            'print_using_pdf_view',
            'Tulostettava myyntiesite löytyy Ladattavat aineistot -osiosta.',
          )}
        </Text>
      </Box>
      <PropertyTabsProvider listing={listing}>
        <ArticleLayout noOverlap noPrint>
          <Container maxWidth="1440px" p={0} layerStyle="fullWidth">
            <Flex
              flexDir="row"
              flexWrap="wrap"
              sx={{ '@media print': { flexWrap: 'nowrap' } }}
            >
              <Box
                width={['100%', null, null, '75%']}
                background="gray.200"
                position="relative"
                className="custom-slick"
              >
                <CustomSwiper
                  key={listing.public_id}
                  location={
                    listing.location
                      ? (listing.location as GeoJSON.GeoJSON)
                      : null
                  }
                  video={listing.video_url}
                  images={propertyImages.map((image) => ({
                    src:
                      publicContainerAttachmentUrl(image.attachment_public) ||
                      '',
                    blurdata_url:
                      image.attachment_public.blurdata_url || undefined,
                    // prepend the property name to the image caption in case there
                    // are multiple properties
                    caption: [
                      listing.properties.length > 1
                        ? image.property_name
                        : null,
                      image.caption,
                    ]
                      .filter(Boolean)
                      .join(': '),
                  }))}
                  title={listing.name || ''}
                />
              </Box>

              <Box
                width={['100%', null, null, '25%']}
                maxWidth={[null, null, null, '390px']}
                sx={{ '@media print': { width: '50%' } }}
              >
                <ListingInfoCard listing={listing} forestData={forestData} />
              </Box>
            </Flex>
          </Container>
          <FieldsLayout bg="white">
            <Section
              title={t('description', 'Kuvaus')}
              pt={sectionPadding}
              as="div"
            >
              <Text whiteSpace="pre-line" fontSize="xl" mb={10}>
                {listing.description_short}
              </Text>
              <Paragraph
                whiteSpace="pre-line"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(listing.description || '', {
                    USE_PROFILES: { html: true },
                  }),
                }}
              />
            </Section>
            <ListingDetailsSection listing={listing} />
            <ListingDetailsTable listing={listing} />
            {forestData && (
              <ForestInformationSection
                forestData={forestData}
                listing={listing}
                className="print-background"
              />
            )}
            <AttachmentsSection
              listing={listing}
              as="section"
              isPreview={isPreview}
            />
            <ListingBuyerCosts listing={listing} />
            <QuestionSection listing={listing} as="section" />
            <Section
              bg="highlightBackground"
              id="contact"
              data-no-gap
              as="aside"
              py={sectionPadding}
              title={t('contact_us', 'Ota yhteyttä')}
            >
              <ContactBlock
                listing={listing}
                sx={{
                  '@media print': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    '.chakra-avatar': { float: 'left', mr: 3 },
                    '.contact-person': { pt: 4, flex: '1 1 auto' },
                  },
                }}
              />
            </Section>
            <FieldsLayout as="section" sectionsLayout>
              {nearbyListings.length > 0 && (
                <Box>
                  <Heading mb={10} size="lg">
                    {t('closest_to_this_listing', 'Lähimpänä tätä kohdetta')}
                  </Heading>
                  <SimpleGrid columns={[1, null, 2, 4]} spacing={5} mb={10}>
                    {nearbyListings?.map((ls) => (
                      <ListingCard listing={ls} key={ls.public_id} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
              {sections}
            </FieldsLayout>
          </FieldsLayout>
        </ArticleLayout>
      </PropertyTabsProvider>
    </>
  );
};

SingleListingPage.layout = PublicLayout;

export default SingleListingPage;

// This function gets called at build time
export const getStaticPaths: GetStaticPaths = async ({
  locales = ['fi'],
}: GetStaticPathsContext) => {
  try {
    if (process.env.BUILD_STATIC_LISTING_PAGES !== 'true') {
      throw new Error(
        'BUILD_STATIC_LISTING_PAGES is not "true". Not generating static listing pages.',
      );
    }

    const publicListings = await fetchAPI<
      GetPublicListingIdsQuery,
      GetPublicListingIdsQueryVariables
    >(
      GetPublicListingIdsDocument,
      {},
      {
        'content-type': 'application/json',
        'x-hasura-admin-secret': process.env.HASURA_GRAPHQL_ADMIN_SECRET || '',
      },
    );

    return {
      paths: locales.flatMap((locale) =>
        publicListings.listing_public
          .filter((listing) => !!listing.public_id)
          .map((listing) => ({
            params: {
              listing: listing.public_id || '',
            },
            locale,
          })),
      ),
      fallback: 'blocking',
    };
  } catch (err) {
    console.error(err);
    Sentry.captureException(err);
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
};

// This also gets called at build time
export const getStaticProps: GetStaticProps = async (context) => {
  const client = initializeApollo();
  // params contains the post `id`.
  // If the route is like /posts/1, then params.id is 1
  // const res = await fetch(`https://.../posts/${params.id}`)
  // const post = await res.json()

  try {
    const {
      params: { listing: id } = {},
      preview,
      locale: lang = 'fi',
      previewData,
    } = context;

    const listingEntryP = fetchEntry({
      slug: 'osta/listing',
      lang,
    }).then((p) =>
      resolveEntryStaticData(p, { ...context, previewData: undefined }),
    );

    const navigationsP = fetchNavigations({
      lang,
    });

    const stringTranslationsP = serverSideTranslations(lang, [
      'frontoffice',
      'generated_pdfs',
      'backoffice',
      'hasura_errors',
    ]);
    /* TODO: either
     * a) tweak the preview query so that it only
     *    returns properties attachments which are public
     *    for sure (atm. everyone logged in authorised
     *    to see all attachments here)
     * b) query attachments separately in the component
     *    (client side only, so many tears will be shed)
     * c) remove this TODO and be content with the quick
     *    fix applied in AttachmentSection/Block, which
     *    is basically just filtering the data.
     */
    const {
      data: { listing_public: listings },
      errors,
    } = await client.query<GetPublicListingByPkQuery>({
      query: GetPublicListingByPkDocument,
      fetchPolicy: 'network-only',
      variables: {
        public_id: id,
        where: preview ? {} : { is_public: { _eq: true } },
      },
      context: {
        headers: {
          'content-type': 'application/json',
          ...(preview
            ? {
                Authorization: `Bearer ${previewData as string}`,
              }
            : {
                'x-hasura-admin-secret':
                  process.env.HASURA_GRAPHQL_ADMIN_SECRET,
              }),
        },
      },
    });

    if (errors) {
      console.error(errors);
      Sentry.captureException(errors);

      if (!preview) {
        throw new Error('Stale while critical data errored');
      }
    }

    if (!listings.length) {
      return {
        notFound: true,
        revalidate: getRevalidateValue(480),
      };
    }
    const [listing] = listings;

    const forestData = await getListingForestryData(
      listing,
      isValidLocale(context.locale) ? context.locale : ForestDataLocales.fi,
    );

    const nearbyListings: GetNearbyListingsQuery['nearby_listings'] = [];
    // Don't try to fetch nearby locations if current listing doesn't have location
    // TMP: remove nearby listings query to see if it affects performance
    // if (listing.location) {
    //   try {
    //     const nearbyResponse = await client.query<GetNearbyListingsQuery>({
    //       query: GetNearbyListingsDocument,
    //       fetchPolicy: 'network-only',
    //       variables: {
    //         id: listing.id,
    //       },
    //         context: {
    //           headers: {
    //             'content-type': 'application/json',
    //             'x-hasura-admin-secret': process.env.HASURA_GRAPHQL_ADMIN_SECRET,
    //           },
    //         },
    //     });
    //     nearbyListings = nearbyResponse.data?.nearby_listings;
    //   } catch (err) {
    //     console.error(err);
    //     Sentry.captureException(err);
    //   }
    // }

    const [staticEntry, stringTranslations, navigations] = await Promise.all([
      listingEntryP,
      stringTranslationsP,
      navigationsP,
    ]);

    // Pass post data to the page via props
    return {
      props: {
        ...stringTranslations,
        navigations: navigations || null,
        sectionFields: staticEntry?.fields
          ? splitFields(staticEntry?.fields).sectionFields
          : [],
        listing: listing || null,
        forestData,
        nearbyListings,
        isPreview: Boolean(preview),
        initialApolloState: client.cache.extract(),
      },
      // Re-generate the post at most once per second
      // if a request comes in
      revalidate: getRevalidateValue(),
    };
  } catch (err) {
    console.error(err);
    Sentry.captureException(err);
    // Keep the listing stale if errored
    throw new Error(
      `Listing page for ${JSON.stringify(
        context.params?.listing,
      )} stale while no data`,
    );
  }
};
